import { Component, h, Host, Element, State } from '@stencil/core';
import { HTMLStencilElement, Prop } from '@stencil/core/internal';
import { WindowResizeService } from '../../services/window-resize-service';
import { ContentVerticalAlignments, ResponsiveString, ContentLineTextAlignOptions } from '../../typings';
import { objectToCssString } from '../../utils/utils';

/**
 * @slot content - This slot will usually serve a text block.
 * @slot footer - This slot is placed below the content slot
 * @slot - Default slot, same as content slot
 */
@Component({
  tag: 'content-text-block',
  styleUrl: 'content-text-block.scss',
  shadow: false,
})
export class ContentTextBlock {
  /**
   * This is a pipe separated string representing the style class.
   * @type String
   * @responsive
   */
  @Prop() classes: ResponsiveString;
  @State() classString;

  /**
   * This is a pipe separated string representing the minimum height. Units without values default to pixels.
   * @type String
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  minHeight: ResponsiveString;

  /**
   * Pipe separated list of ContentVerticalAlignments for text box placement.
   * @type Select
   * @options top
   * @options center
   * @options bottom
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  vertical: ContentVerticalAlignments = 'top';

  /**
   * @type Boolean
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  fullHeight: ResponsiveString = 'false';

  /**
   * Pipe separated list of ContentLineTextAlignOptions for text alignment.
   * @type Select
   * @options default
   * @options left
   * @options center
   * @options right
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  textAlign: ContentLineTextAlignOptions = 'default';
  @Element() hostElement: HTMLStencilElement;
  @State() inlineStyle?: { [key: string]: string } = {};
  componentWillLoad() {
    if (!!this.hostElement.querySelector('[slot="footer"]')) {
      this.hostElement.setAttribute('footer', 'true');
    }
    WindowResizeService.registerProp(this.minHeight).subscribe(newValue => {
      this.inlineStyle = { ...this.inlineStyle, 'min-height': isNaN(newValue as any) ? (newValue as string) : newValue + 'px' };
    });
    WindowResizeService.registerProp(this.textAlign).subscribe(newValue => this.hostElement.setAttribute('text-align', newValue as ContentLineTextAlignOptions));
    WindowResizeService.registerProp(this.vertical).subscribe(newValue => this.hostElement.setAttribute('vertical', newValue as ContentVerticalAlignments));
    WindowResizeService.registerProp(this.fullHeight).subscribe(newValue => {
      this.hostElement.setAttribute('full-height', newValue as string);
    });
    WindowResizeService.registerProp(this.classes).subscribe(newValue => (this.classString = newValue as string));
  }
  render() {
    return (
      <Host class={this.classString} style={this.inlineStyle}>
        <div class="content-text-block__content">
          <slot name="content"></slot>
          <slot></slot>
        </div>
        <slot name="footer"></slot>
      </Host>
    );
  }
}
